// 印字文字キーを消費しない input の type 集合。
// 未知の type はブラウザが "text" へ正規化するため、集合に無い種別は文字入力ありとして安全側に倒す。
const NON_CHARACTER_INPUT_TYPES: ReadonlySet<string> = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

// Connect Feedback ミニウィンドウ（document.body へ portal する別 UI）の内側か。
// キャンバスのショートカットは window / document の keydown（capture 段階のものもある）で、
// フォーカス位置に依存せず発火する。ミニウィンドウ側で全キーの伝播を止めるとブラウザーの
// 既定操作まで巻き込むため、フォーム入力と同じ「編集中」として扱い個別に抑止する。
// 別 origin の iframe へフォーカスがある場合も、親から見た activeElement は iframe 要素であり
// この判定に一致するため、クリップボード操作の横取りも同時に防げる。
function isConnectFeedbackWindowTarget(target: HTMLElement): boolean {
  return target.closest("[data-connect-feedback-window]") !== null;
}

// フォーム入力コントロール全般にフォーカスがあるか。
// 矢印キーや Space を消費するコントロールも含め、パン・削除・クリップボード操作を保護する。
export function isEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  // 付箋・テキストは contentEditable で実装されるため、タグ名より先に判定する。
  if (target.isContentEditable) return true;
  if (isConnectFeedbackWindowTarget(target)) return true;
  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select";
}

// native イベント（copy / cut / paste / keydown）が「本文編集の文脈」に属するか。
// 直接の event.target だけを見ると、contentEditable の子要素から伝播した場合や、
// ブラウザが target を retarget した場合に編集要素を取りこぼし、キャンバス側の
// クリップボード処理（オブジェクトのコピー / 切り取り / OGP カード作成）が
// 本文編集を奪ってしまう。伝播経路と現在のフォーカスの論理和で判定する。
export function isEditingEventContext(event: Event): boolean {
  // 1) 直接の対象。composedPath が利用できない環境でも、従来の単一対象判定より
  //    保護が狭まらないことを保証する。
  if (isEditingTarget(event.target)) return true;
  const path = composedPathOf(event);
  // 2) 伝播経路に編集要素があれば、その編集ホストが所有するイベントとして扱う。
  for (const entry of path) {
    if (isEditingTarget(entry)) return true;
  }
  // 3) 経路で判定できない場合は、そのイベントが属する document の現在のフォーカスを見る。
  //    iframe 等の別 document を誤って参照しないよう、イベント側から特定できた document を
  //    グローバル document より優先する。
  const ownerDocument = resolveDocument(path, event.target);
  return isEditingTarget(ownerDocument?.activeElement ?? null);
}

// 伝播経路の取得。合成イベントや古い実装では composedPath が無い / 例外を投げることがあるため、
// 取得できない場合は空配列として扱い、activeElement 側の判定へ進ませる。
function composedPathOf(event: Event): readonly EventTarget[] {
  try {
    const path = event.composedPath?.();
    return Array.isArray(path) ? path : [];
  } catch {
    return [];
  }
}

// イベントが属する document を、経路内の Document または DOM Node の ownerDocument から特定する。
// どちらからも得られない場合だけ、ブラウザ環境の現在 document をフォールバックとして使う。
function resolveDocument(
  path: readonly EventTarget[],
  target: EventTarget | null
): Document | null {
  for (const entry of path) {
    const found = documentOf(entry);
    if (found) return found;
  }
  const fromTarget = documentOf(target);
  if (fromTarget) return fromTarget;
  return typeof document !== "undefined" ? document : null;
}

function documentOf(entry: EventTarget | null): Document | null {
  if (entry === null) return null;
  // Document 自身は Node でもあるが ownerDocument が null のため、先に判定する。
  if (typeof Document !== "undefined" && entry instanceof Document) return entry;
  if (typeof Node !== "undefined" && entry instanceof Node) return entry.ownerDocument;
  return null;
}

// 印字文字キーを消費する対象にフォーカスがあるか。
// 単一キーショートカットでは、色入力やスライダーなど文字を受け付けない input を抑止しない。
export function isCharacterInputTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  // 単一キーのツール切替（付箋・図形など）も、ミニウィンドウ操作中は発火させない。
  // この判定は isEditingTarget とは独立に使われるため、両方へ入れる必要がある。
  if (isConnectFeedbackWindowTarget(target)) return true;

  const tag = target.tagName.toLowerCase();
  if (tag === "textarea" || tag === "select") return true;
  if (tag !== "input") return false;

  return !NON_CHARACTER_INPUT_TYPES.has((target as HTMLInputElement).type);
}
